import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useMemo, useState } from 'react';

import { Button } from '../../components/ui/Button.jsx';
import { Modal } from '../../components/ui/Modal.jsx';
import { useTranslation } from '../../i18n/useTranslation.js';
import { financeApi } from '../../services/financeApi.js';
import { formatCurrency } from '../../utils/formatters.js';

const EMPTY = {
  name: '', category: '', acquisitionDate: '', acquisitionCost: '0', salvageValue: '0',
  usefulLifeMonths: '12', method: 'LINEAR', decliningRate: '',
  assetAccountId: '', depreciationExpenseAccountId: '', accumulatedDepreciationAccountId: '',
  fundingAccountId: '', notes: '',
};

// Integer-rappen first-period preview - mirrors the backend schedule math so the
// figure shown matches what posts (CLAUDE.md: previews use identical cents math).
const toCents = (v) => Math.round(Number(v || 0) * 100);
function firstPeriodCents(form) {
  const cost = toCents(form.acquisitionCost);
  const dep = cost - toCents(form.salvageValue);
  const n = Number(form.usefulLifeMonths) || 0;
  if (dep <= 0 || n <= 0) return null;
  if (form.method === 'DECLINING_BALANCE') {
    const rate = Number(form.decliningRate) || 0;
    if (rate <= 0) return null;
    return Math.round(cost * (rate / 100 / 12));
  }
  return Math.floor(dep / n);
}

export function AssetCreateModal({ open, onClose }) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [form, setForm] = useState(EMPTY);
  const [error, setError] = useState(null);

  const accountsQuery = useQuery({
    queryKey: ['finance', 'accounts'],
    queryFn: () => financeApi.listAccounts().then((r) => r.items),
    enabled: open,
  });
  const accounts = accountsQuery.data || [];
  const assetAccounts = accounts.filter((a) => a.type === 'ASSET');
  const expenseAccounts = accounts.filter((a) => a.type === 'EXPENSE');

  /* Die Vorbelegung kommt aus den Systemkonten, nicht aus Kontonummern.
     Vorher standen hier 1600, 6800 und 1601 fest im Code. Nach der Umstellung
     auf den Kontenrahmen KMU ist 1600 nicht mehr das Anlagenkonto - die
     Maschine steht auf 1500. Eine fest verdrahtete Nummer haette entweder
     nichts gefunden oder, schlimmer, das falsche Konto vorbelegt. */
  const systemQuery = useQuery({
    queryKey: ['finance', 'accounts', 'system'],
    queryFn: () => financeApi.systemAccounts().then((r) => r.items),
    enabled: open,
  });

  useEffect(() => {
    if (!open) return;
    const zweck = (purpose) => (systemQuery.data || []).find((z) => z.purpose === purpose)?.accountId || '';
    setForm((f) => ({
      ...EMPTY,
      ...f,
      assetAccountId: f.assetAccountId || zweck('ASSET_FIXED'),
      depreciationExpenseAccountId: f.depreciationExpenseAccountId || zweck('DEPRECIATION_EXPENSE'),
      accumulatedDepreciationAccountId:
        f.accumulatedDepreciationAccountId || zweck('ACCUMULATED_DEPRECIATION'),
    }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, systemQuery.data]);

  useEffect(() => {
    if (open) { setForm(EMPTY); setError(null); }
  }, [open]);

  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));
  const preview = useMemo(() => firstPeriodCents(form), [form]);

  const save = useMutation({
    mutationFn: (body) => financeApi.createFixedAsset(body),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['finance', 'fixed-asset-register'] });
      queryClient.invalidateQueries({ queryKey: ['finance', 'fixed-asset-insights'] });
      onClose();
    },
    onError: (err) => setError(err?.message || t('common.loadError')),
  });

  function submit(e) {
    e.preventDefault();
    setError(null);
    save.mutate({
      name: form.name,
      category: form.category || null,
      acquisitionDate: form.acquisitionDate,
      acquisitionCost: form.acquisitionCost,
      salvageValue: form.salvageValue || '0',
      usefulLifeMonths: Number(form.usefulLifeMonths),
      method: form.method,
      decliningRate: form.method === 'DECLINING_BALANCE' ? Number(form.decliningRate) : null,
      assetAccountId: form.assetAccountId,
      depreciationExpenseAccountId: form.depreciationExpenseAccountId,
      accumulatedDepreciationAccountId: form.accumulatedDepreciationAccountId,
      fundingAccountId: form.fundingAccountId || null,
      notes: form.notes || null,
    });
  }

  const accountOption = (a) => (
    <option key={a.id} value={a.id}>{a.code} · {a.name}</option>
  );

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={t('fa.modal.title')}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>{t('common.cancel')}</Button>
          <Button variant="primary" onClick={submit} disabled={save.isPending}>
            {save.isPending ? `${t('common.save')}…` : t('common.save')}
          </Button>
        </>
      }
    >
      <form onSubmit={submit} className="form-stack">
        <label className="field">
          <span className="field-label">{t('fa.field.name')}</span>
          <input className="input" value={form.name} onChange={set('name')} required />
        </label>
        <div className="field-row">
          <label className="field">
            <span className="field-label">{t('fa.field.category')}</span>
            <input className="input" value={form.category} onChange={set('category')} />
          </label>
          <label className="field">
            <span className="field-label">{t('fa.field.acquisitionDate')}</span>
            <input className="input" type="date" value={form.acquisitionDate} onChange={set('acquisitionDate')} required />
          </label>
        </div>
        <div className="field-row">
          <label className="field">
            <span className="field-label">{t('fa.field.cost')}</span>
            <input className="input" type="number" min="0" step="0.01" value={form.acquisitionCost} onChange={set('acquisitionCost')} required />
          </label>
          <label className="field">
            <span className="field-label">{t('fa.field.salvage')}</span>
            <input className="input" type="number" min="0" step="0.01" value={form.salvageValue} onChange={set('salvageValue')} />
          </label>
        </div>
        <div className="field-row">
          <label className="field">
            <span className="field-label">{t('fa.field.usefulLife')}</span>
            <input className="input" type="number" min="1" step="1" value={form.usefulLifeMonths} onChange={set('usefulLifeMonths')} required />
          </label>
          <label className="field">
            <span className="field-label">{t('fa.field.method')}</span>
            <select className="input" value={form.method} onChange={set('method')}>
              <option value="LINEAR">{t('fa.method.LINEAR')}</option>
              <option value="DECLINING_BALANCE">{t('fa.method.DECLINING_BALANCE')}</option>
            </select>
          </label>
          {form.method === 'DECLINING_BALANCE' && (
            <label className="field">
              <span className="field-label">{t('fa.field.decliningRate')}</span>
              <input className="input" type="number" min="0" max="100" step="0.1" value={form.decliningRate} onChange={set('decliningRate')} required />
            </label>
          )}
        </div>

        <label className="field">
          <span className="field-label">{t('fa.field.assetAccount')}</span>
          <select className="input" value={form.assetAccountId} onChange={set('assetAccountId')} required>
            <option value="">-</option>{assetAccounts.map(accountOption)}
          </select>
        </label>
        <div className="field-row">
          <label className="field">
            <span className="field-label">{t('fa.field.expenseAccount')}</span>
            <select className="input" value={form.depreciationExpenseAccountId} onChange={set('depreciationExpenseAccountId')} required>
              <option value="">-</option>{expenseAccounts.map(accountOption)}
            </select>
          </label>
          <label className="field">
            <span className="field-label">{t('fa.field.accumulatedAccount')}</span>
            <select className="input" value={form.accumulatedDepreciationAccountId} onChange={set('accumulatedDepreciationAccountId')} required>
              <option value="">-</option>{assetAccounts.map(accountOption)}
            </select>
          </label>
        </div>
        <label className="field">
          <span className="field-label">{t('fa.field.fundingAccount')}</span>
          <select className="input" value={form.fundingAccountId} onChange={set('fundingAccountId')}>
            <option value="">{t('fa.field.fundingNone')}</option>{accounts.map(accountOption)}
          </select>
        </label>

        {preview != null && (
          <div className="alert info">{t('fa.preview.firstPeriod', { amount: formatCurrency(preview / 100) })}</div>
        )}
        {error && <div className="alert danger">{error}</div>}
      </form>
    </Modal>
  );
}
